/**
 * Currency Role - Cast Pillar
 *
 * Normaliza input monetário sujo para número.
 * Extrai o valor numérico de strings formatadas como moeda.
 *
 * NÃO infere a moeda - apenas extrai o valor numérico.
 * A moeda deve ser conhecida via metadata.
 *
 * @example
 * import { castCurrency, castCurrencyStrict } from '@attrx/role-morphic/currency/cast';
 *
 * castCurrency('R$ 1.500,50');     // 1500.50
 * castCurrency('$1,000.00');       // 1000
 * castCurrency('€ 99,99');         // 99.99
 * castCurrency('1500');            // 1500
 * castCurrency(1500.50);           // 1500.50
 */

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input monetário para número
 *
 * Suporta diversos formatos:
 * - Brasileiro: R$ 1.500,50
 * - Americano: $1,000.00
 * - Europeu: € 99,99 ou 99,99 €
 * - Sem símbolo: 1500.50 ou 1500,50
 *
 * @param input - Valor de entrada (number, string, etc)
 * @returns Número ou null se não puder converter
 *
 * @example
 * castCurrency('R$ 1.500,50');     // 1500.50
 * castCurrency('$1,000.00');       // 1000
 * castCurrency('€ 99,99');         // 99.99
 * castCurrency('1.500,50');        // 1500.50 (brasileiro)
 * castCurrency('1,500.50');        // 1500.50 (americano)
 * castCurrency('-R$ 50,00');       // -50
 * castCurrency('invalid');         // null
 */
export function castCurrency(input: unknown): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    return parseCurrencyString(input);
  }

  return null;
}

/**
 * Alias para castCurrency (currency não tem conversão, então strict é igual)
 */
export function castCurrencyStrict(input: unknown): number | null {
  return castCurrency(input);
}

/**
 * Versão safe de castCurrency que retorna Result
 */
export function tryCastCurrency(input: unknown): Result<number> {
  const result = castCurrency(input);
  if (result === null) {
    return {
      ok: false,
      error: `Cannot cast "${String(input)}" to currency value`,
    };
  }
  return { ok: true, value: result };
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Símbolos de moeda conhecidos (para remoção no parse)
 */
const CURRENCY_SYMBOL_REGEX = /^[\s]*([A-Z]{1,3}\$?|[R$€£¥₹₩₽₺CHF]+)[\s]*/i;
const CURRENCY_SYMBOL_SUFFIX_REGEX = /[\s]*([€₽])[\s]*$/;

/**
 * Parseia string monetária para número
 */
function parseCurrencyString(input: string): number | null {
  let str = input.trim();

  if (!str) {
    return null;
  }

  // Detecta sinal negativo (pode estar antes do símbolo)
  let isNegative = false;
  if (str.startsWith("-")) {
    isNegative = true;
    str = str.slice(1).trim();
  } else if (str.startsWith("(") && str.endsWith(")")) {
    // Formato contábil: (1,000.00) = -1000
    isNegative = true;
    str = str.slice(1, -1).trim();
  }

  // Remove símbolo de moeda do início
  str = str.replace(CURRENCY_SYMBOL_REGEX, "");

  // Remove símbolo de moeda do final (ex: 100 €)
  str = str.replace(CURRENCY_SYMBOL_SUFFIX_REGEX, "");

  str = str.trim();

  if (!str) {
    return null;
  }

  // Parseia o número
  const numValue = parseNumber(str);

  if (numValue === null) {
    return null;
  }

  return isNegative ? -numValue : numValue;
}

/**
 * Parseia string numérica considerando formatos brasileiro e americano
 *
 * Detecta automaticamente o formato baseado nos separadores:
 * - 1.000,50 → brasileiro → 1000.50
 * - 1,000.50 → americano → 1000.50
 * - 1.000 → ambíguo (assume milhar) → 1000
 * - 1,50 → brasileiro decimal → 1.50
 */
function parseNumber(numStr: string): number | null {
  // Remove espaços
  numStr = numStr.replace(/\s/g, "");

  // Remove caracteres não numéricos exceto . , - +
  if (!/^[+-]?[\d.,]+$/.test(numStr)) {
    return null;
  }

  const lastComma = numStr.lastIndexOf(",");
  const lastDot = numStr.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      // Brasileiro: 1.000,50 → vírgula é decimal
      numStr = numStr.replace(/\./g, "").replace(",", ".");
    } else {
      // Americano: 1,000.50 → ponto é decimal
      numStr = numStr.replace(/,/g, "");
    }
  } else if (hasComma && !hasDot) {
    // Só vírgulas: detecta se é milhar ou decimal
    const parts = numStr.split(",");
    const afterComma = parts.slice(1);

    // Se tem múltiplas vírgulas OU parte após vírgula tem 3 dígitos = milhar
    if (afterComma.length > 1 || (afterComma.length === 1 && afterComma[0].length === 3 && parts[0].length <= 3)) {
      // Milhar brasileiro: 1,000,000 ou 1,000
      numStr = numStr.replace(/,/g, "");
    } else {
      // Decimal brasileiro: 1,50
      numStr = numStr.replace(",", ".");
    }
  } else if (!hasComma && hasDot) {
    // Só pontos
    const parts = numStr.split(".");
    const afterDot = parts.slice(1);

    // Múltiplos pontos = milhar brasileiro: 1.000.000
    if (afterDot.length > 1) {
      numStr = numStr.replace(/\./g, "");
    } else if (afterDot.length === 1 && afterDot[0].length === 3) {
      // Único ponto com exatamente 3 dígitos após = milhar (1.500 → 1500)
      // Em contexto monetário, 1.500 é quase sempre milhar, não 1.5
      numStr = numStr.replace(/\./g, "");
    }
    // Outros casos: decimal americano (1.50, 1000.50)
  }

  const value = parseFloat(numStr);
  return isNaN(value) ? null : value;
}
